import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const isHomepage = location.pathname === '/';
  const showTransparent = isHomepage && !scrolled;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(e.target as Node)) {
        setGuidesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const textColor = showTransparent ? 'text-white' : 'text-foreground-900';
  const borderColor = showTransparent ? 'border-white/40 hover:border-white/80' : 'border-foreground-300 hover:border-foreground-500';
  const pillBg = showTransparent ? 'bg-transparent' : 'bg-white';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        showTransparent ? 'bg-transparent' : 'navbar-scrolled'
      }`}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Brand */}
          <Link
            to="/"
            className={`font-heading text-lg md:text-2xl font-bold tracking-tight whitespace-nowrap transition-colors duration-300 ${textColor}`}
          >
            Kakobuy Spreadsheet
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-3">
            {/* Home */}
            <Link
              to="/"
              className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                showTransparent
                  ? 'text-white hover:bg-white/10'
                  : 'text-foreground-700 hover:bg-background-100'
              } ${isHomepage ? (showTransparent ? 'bg-white/10' : 'bg-background-100') : ''}`}
            >
              Home
            </Link>

            {/* Category Dropdown */}
            <div className="relative" ref={categoryRef}>
              <button
                onClick={() => { setCategoryOpen(!categoryOpen); setGuidesOpen(false); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${borderColor} ${pillBg} ${textColor}`}
              >
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {categoryOpen && (
                <div className="absolute top-full mt-2 right-0 w-64 bg-white rounded-xl border border-background-200 overflow-hidden max-h-80 overflow-y-auto shadow-lg">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                      onClick={() => setCategoryOpen(false)}
                    >
                      {cat.displayName}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guides Dropdown */}
            <div className="relative" ref={guidesRef}>
              <button
                onClick={() => { setGuidesOpen(!guidesOpen); setCategoryOpen(false); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${borderColor} ${pillBg} ${textColor}`}
              >
                <span>Shopping Guides</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${guidesOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guidesOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-white rounded-xl border border-background-200 overflow-hidden max-h-80 overflow-y-auto shadow-lg">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/guides/${article.slug}`}
                      className="block px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150"
                      onClick={() => setGuidesOpen(false)}
                    >
                      <span className="block font-medium text-foreground-900">{article.title}</span>
                      <span className="block text-xs text-foreground-500 mt-0.5">{article.readTime}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* CTA Buttons */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2.5 rounded-full border text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                showTransparent
                  ? 'border-white/60 text-white hover:bg-white/10'
                  : 'border-primary-500 text-primary-600 hover:bg-primary-50'
              }`}
            >
              kakobuy spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2.5 rounded-full bg-accent-500 text-white text-sm font-medium hover:bg-accent-600 transition-all duration-200 whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${textColor}`}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu - improved */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            {/* Quick nav */}
            <div className="flex gap-2">
              <Link
                to="/"
                className="flex-1 px-4 py-2.5 rounded-full bg-background-100 text-foreground-900 text-sm font-semibold text-center hover:bg-background-200 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 px-4 py-2.5 rounded-full bg-accent-500 text-white text-sm font-semibold text-center hover:bg-accent-600 transition-all"
                onClick={() => setMobileMenuOpen(false)}
              >
                Buy It Now
              </a>
            </div>

            {/* Categories Section */}
            <div>
              <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2 px-1">Product Categories</p>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {cat.displayName}
                  </a>
                ))}
              </div>
            </div>

            {/* Guides Section */}
            <div>
              <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-2 px-1">Shopping Guides</p>
              <div className="space-y-0.5">
                {articles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/guides/${article.slug}`}
                    className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="font-medium text-foreground-900">{article.title}</span>
                    <span className="block text-xs text-foreground-500">{article.readTime}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Bottom CTA */}
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="block w-full px-4 py-3 rounded-full border border-primary-500 text-primary-600 text-sm font-semibold text-center hover:bg-primary-50 transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              kakobuy spreadsheet
            </a>
          </div>
        </div>
      )}

      {/* Mobile bottom sticky category bar */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-background-200 px-2 py-2 overflow-x-auto">
        <div className="flex gap-2 min-w-max px-1">
          {categories.slice(0, 7).map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 px-3 py-1.5 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-primary-100 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              {cat.displayName}
            </a>
          ))}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="flex-shrink-0 px-3 py-1.5 rounded-full bg-primary-500 text-white text-xs font-medium whitespace-nowrap cursor-pointer"
          >
            More +
          </button>
        </div>
      </div>
    </nav>
  );
}